import { useState } from 'react';
import { ChevronDown, Clock } from 'lucide-react';

interface DateSelectorProps {
    onDateChange: (utcDate: string) => void;
}

const SingleDateRangeSelector = ({ onDateChange }: DateSelectorProps) => {
    const [selectedRange, setSelectedRange] = useState('last_24h');
    const [isOpen, setIsOpen] = useState(false);
    const [customDate, setCustomDate] = useState('');

    const calculateUTC = (range: string, manualDate?: string) => {
        const now = new Date();
        let resultDate = new Date();
        if (range === 'last_24h') resultDate.setHours(now.getHours() - 24);
        else if (range === 'last_week') resultDate.setDate(now.getDate() - 7);
        else if (range === 'custom' && manualDate) {
            resultDate = new Date(manualDate);
            resultDate.setUTCHours(0, 0, 0, 0);
        }
        return resultDate.toISOString().split('.')[0];
    };

    const handleSelect = (range: string, date?: string) => {
        setSelectedRange(range);
        if (range !== 'custom' || date) {
            onDateChange(calculateUTC(range, date));
            setIsOpen(false);
        }
    };

    return (
        <div className="relative inline-block w-30 text-[8px]">
            {/* Compact Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full bg-gray-800 border border-slate-800 rounded-lg px-2.5 py-1.5 text-slate-300 hover:border-orange-500/50 transition-all shadow-md"
            >
                <div className="flex items-center gap-2 truncate text-xs">
                    <Clock className="w-2 h-2 text-orange-500 flex-shrink-0" />
                    <span className="truncate">
                        {selectedRange === 'custom' && customDate ? customDate : (selectedRange === 'last_24h' ? '24 Hours' : 'Last Week')}
                    </span>
                </div>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Compact Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-slate-800 rounded-lg shadow-2xl p-1.5 animate-in fade-in zoom-in duration-150">
                    {/* Horizontal Preset Row */}
                    <div className="flex gap-1 mb-2">
                        <button
                            onClick={() => handleSelect('last_24h')}
                            className={`flex-1 py-1 rounded-md font-bold transition-colors ${selectedRange === 'last_24h' ? 'bg-orange-500/20 text-orange-500' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300'}`}
                        >
                            24H
                        </button>
                        <button
                            onClick={() => handleSelect('last_week')}
                            className={`flex-1 py-1 rounded-md font-bold transition-colors ${selectedRange === 'last_week' ? 'bg-orange-500/20 text-orange-500' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300'}`}
                        >
                            1W
                        </button>
                    </div>

                    {/* Custom Input Section */}
                    <div className="pt-2 border-t border-slate-800/50">
                        <input
                            type="date"
                            className="w-full bg-transparent border-none text-slate-300 text-[10px] outline-none mb-2 cursor-pointer"
                            onChange={(e) => { setCustomDate(e.target.value); setSelectedRange('custom'); }}
                        />
                        <button
                            onClick={() => handleSelect('custom', customDate)}
                            disabled={!customDate}
                            className="w-full bg-orange-600 hover:bg-orange-500 disabled:opacity-30 text-white font-bold py-1.5 rounded-md transition-all uppercase tracking-tighter"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleDateRangeSelector;
